import {
    createComponentById,
    NO_PATTERN_BUSINESS_LOGIC,
} from '@vfde-brix/ws10/core';
import {
    createOfferSummaryCard,
    OFFER_SUMMARY_CARD_HEADLINE_CLASSNAME,
    OfferSummaryCard,
} from '@vfde-brix/ws10/offer-summary-card';
import { HtmlId } from '../enums/elementId';
import { JarvisProduct } from '@vfde-sails/availability';
import { getHeadlineUpdatedProps } from '../util/components/headline.util';
import {
    getLegalTexts,
    getTariffSublineText,
} from '../util/components/offerSummaryCard.util';
import { getElementByBrixClassName } from '../util/brixElement';
import {
    FOOTNOTE_LINK_CLASSNAME,
    mountFootnoteLinks,
} from './footnote';
import { HEADLINE_BASE_CLASSNAME } from '@vfde-brix/ws10/headline';
import { Customer } from '@vfde-sails/login';
import { getSummaryCardUpdatedButtonProps } from '../util/components/button.util';
import {
    getSummaryCardUpdatedAdditionalPriceProps,
    getSummaryCardUpdatedPriceProps,
} from '../util/components/offerSummaryCard.util';
import { setDisplayMetacode } from '@vfde-sails/utils';
import {
    createTooltipInnerHtml,
    mountSummaryCardTooltip,
} from './tooltip';
import { getCMSConfig } from '../util/cms';

/**
 * Creates a WS10 Offer Summary Card component.
 */
export const mountOfferSummaryCard = (id: HtmlId) =>
    createComponentById(createOfferSummaryCard, id, NO_PATTERN_BUSINESS_LOGIC);

const getHeadlineEl = (summaryCard: OfferSummaryCard): Element | undefined => summaryCard
    .getContainerElement()
    ?.getElementsByClassName(OFFER_SUMMARY_CARD_HEADLINE_CLASSNAME)[0]
    ?.getElementsByClassName(HEADLINE_BASE_CLASSNAME)[0];

/**
 * Updates the headline text for the Offer Summary Card component, and
 * re-apply the footnote link element with the proper events.
 *
 * @param summaryCard - The Offer Summary Card component
 * @param tariff - Selected tariff
 * @param tariff.name - New text for the headline
 * @param tariff.id - ID of the selected tariff
 */
const updateHeadline = (summaryCard: OfferSummaryCard, { name, id }: JarvisProduct) => {
    const [supEl] = getHeadlineEl(summaryCard)?.getElementsByClassName(FOOTNOTE_LINK_CLASSNAME) ?? [];

    summaryCard.updateHeadline(getHeadlineUpdatedProps(name));

    supEl && getHeadlineEl(summaryCard)?.appendChild(supEl);
    mountFootnoteLinks(id);
};

const updateSubline = (summaryCard: OfferSummaryCard, selectedTariff: JarvisProduct) => {
    const sublineText = getTariffSublineText(selectedTariff);
    const { tooltip } = getCMSConfig().offerSummaryCard.tariffSubline;
    const uniqueTooltipId = tooltip ? `${tooltip.id}-${selectedTariff.id}`.toLowerCase() : null;
    const tooltipInnerHtml = uniqueTooltipId ? createTooltipInnerHtml(uniqueTooltipId) : '';

    summaryCard.updateSubline([`${sublineText} ${tooltipInnerHtml}`]);

    uniqueTooltipId && mountSummaryCardTooltip(uniqueTooltipId, selectedTariff);
};

const updateDisplayMetacode = (summaryCard: OfferSummaryCard, metacode: string) => {
    const headlineEl = getElementByBrixClassName(summaryCard, OFFER_SUMMARY_CARD_HEADLINE_CLASSNAME);

    headlineEl && setDisplayMetacode(headlineEl, metacode);
};

/**
 * Updates all Offer Summary Card elements based on the current state.
 */
export const updateOfferSummaryCard = (summaryCard: OfferSummaryCard, selectedTariff: JarvisProduct) => {
    summaryCard.updatePrices(
        getSummaryCardUpdatedPriceProps(selectedTariff.price),
        getSummaryCardUpdatedAdditionalPriceProps(selectedTariff.price),
    );
    summaryCard.updateLegalText(getLegalTexts(selectedTariff));

    // Note: Custom updates
    updateHeadline(summaryCard, selectedTariff);
    updateSubline(summaryCard, selectedTariff);
    updateDisplayMetacode(summaryCard, selectedTariff.id);
};

/**
 * Updates the business logic of the CtA button element in the Offer Summary Card.
 *
 * @param summaryCard - The Offer Summary Card component to update
 * @param customer - Logged in customer data if the user is logged in
 * @param onClick - Business logic
 */
export const updateOfferSummaryCardBusiness = (
    summaryCard: OfferSummaryCard,
    customer: Customer | undefined,
    onClick?: () => void,
) => summaryCard.updateButtons([
    getSummaryCardUpdatedButtonProps(customer, onClick),
]);
